import Loading from '@/common/atoms/Loading'
import TextErrors from '@/common/atoms/TextErrors'
import useCallbackState from '@/common/hooks/useCallbackState'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import { useOrgContext } from '@/org/contexts/OrgContext'
import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  UseModalProps,
  useSteps,
  useToast,
} from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trpc } from 'src/trpc'
import MeetingSummaryShareBoard from '../components/MeetingSummaryShareBoard'
import MeetingSummarySharePreview from '../components/MeetingSummarySharePreview'
import MeetingSummaryShareRecipients from '../components/MeetingSummaryShareRecipients'
import {
  ShareBoard,
  canContinue,
  createBoard,
  finalPieces,
  summarySteps,
  syncBoard,
} from '../meetingSummaryShareBoard'
import {
  downloadBase64File,
  notifyMeetingSummaryShared,
} from '../meetingSummaryShareEvents'

interface Props extends UseModalProps {
  meetingId: string
}

// Share the compte rendu outside its circle (#88 + #115): to whom, what, then
// send it by email or download it as a PDF
export default function MeetingSummaryShareModal({
  meetingId,
  ...modalProps
}: Props) {
  const { t } = useTranslation()
  const toast = useToast()
  const { orgData } = useOrgContext()
  const currentMember = useCurrentMember()
  const wizardSteps = ['recipients', 'content', 'preview'] as const
  const { activeStep, goToNext, goToPrevious } = useSteps({
    index: 0,
    count: wizardSteps.length,
  })

  const [circlesIds, setCirclesIds] = useState<string[]>([])
  const [membersIds, setMembersIds] = useState<string[]>([])
  const [board, setBoard] = useState<ShareBoard>()
  const [excludedSteps, setExcludedSteps] = useState<string[]>([])
  const [intro, setIntro] = useState('')
  const [sending, setSending] = useState(false)
  const [downloading, setDownloading] = useState(false)

  // The compte rendu as this member reads it, cut into pieces
  const {
    call: loadPieces,
    value: pieces,
    loading,
    error,
  } = useCallbackState(() => trpc.meeting.getSummaryPieces.query({ meetingId }))
  useEffect(() => {
    loadPieces()
  }, [meetingId])
  // The pieces of the steps kept for this share: a step left out in step 1
  // leaves the board, and is never sent
  const summaryStepsList = useMemo(() => summarySteps(pieces ?? []), [pieces])
  const visiblePieces = useMemo(
    () =>
      (pieces ?? []).filter(
        (p) => !p.step || !excludedSteps.includes(p.step.key)
      ),
    [pieces, excludedSteps]
  )
  useEffect(() => {
    if (!pieces) return
    setBoard((board) =>
      board ? syncBoard(board, visiblePieces) : createBoard(visiblePieces)
    )
  }, [pieces, visiblePieces])

  const handleToggleStep = (key: string) =>
    setExcludedSteps((keys) =>
      keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]
    )

  // Choosing a circle adds its participants to the people, as when inviting a
  // circle to a meeting; leaving it removes those who came only through it
  const participantsOf = (circleId: string) =>
    (orgData?.getParticipants(circleId) ?? [])
      .map(({ member }) => member.id)
      .filter((id) => id !== currentMember?.id)
  const handleChangeCircles = (ids: string[]) => {
    const added = ids.filter((id) => !circlesIds.includes(id))
    const removed = circlesIds.filter((id) => !ids.includes(id))
    let members = [...membersIds]
    for (const circleId of added) {
      for (const id of participantsOf(circleId)) {
        if (!members.includes(id)) members.push(id)
      }
    }
    for (const circleId of removed) {
      const stillCovered = new Set(ids.flatMap(participantsOf))
      const leaving = new Set(participantsOf(circleId))
      members = members.filter((id) => !leaving.has(id) || stillCovered.has(id))
    }
    setCirclesIds(ids)
    setMembersIds(members)
  }

  // The chosen people without an account have no email: named up front, and
  // again when sending (Yohan, 26/09/2026)
  const withoutAccountNames = useMemo(
    () =>
      membersIds
        .map((id) => orgData?.members.find((m) => m.id === id))
        .filter((member) => member && !member.userId)
        .map((member) => member!.name)
        .sort((a, b) => a.localeCompare(b)),
    [orgData, membersIds]
  )

  // Who will receive it: the people of the list, as the server sends it
  const recipientsNames = useMemo(
    () =>
      membersIds
        .map((id) => orgData?.members.find((m) => m.id === id)?.name)
        .filter((name): name is string => !!name)
        .sort((a, b) => a.localeCompare(b)),
    [orgData, membersIds]
  )

  const sentPieces = useMemo(
    () => (board ? finalPieces(board, visiblePieces) : []),
    [board, visiblePieces]
  )
  const previewPieces = useMemo(() => {
    const bodies = new Map(sentPieces.map((p) => [p.id, p.body]))
    return (pieces ?? [])
      .filter((p) => bodies.has(p.id))
      .map((p) => ({ ...p, body: bodies.get(p.id)! }))
  }, [pieces, sentPieces])

  const canGoNext =
    activeStep === 0
      ? recipientsNames.length > 0
      : activeStep === 1
        ? !!board && canContinue(board)
        : false

  const handleError = (error: unknown) => {
    console.error(error)
    toast({
      title: t('common.errorOccurred'),
      status: 'error',
      duration: 3000,
      isClosable: true,
    })
  }

  const handleSend = async () => {
    // Nobody with an account: nothing can leave, say who
    if (withoutAccountNames.length === recipientsNames.length) {
      toast({
        title: t('MeetingSummaryShareModal.noEmailFor', {
          names: withoutAccountNames.join(', '),
        }),
        status: 'error',
        duration: 8000,
        isClosable: true,
      })
      return
    }
    setSending(true)
    try {
      const result = await trpc.meeting.shareSummary.mutate({
        meetingId,
        circleIds: circlesIds,
        memberIds: membersIds,
        intro,
        pieces: sentPieces,
      })
      toast({
        title: t('MeetingSummaryShareModal.sent'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      if (result.withoutEmail.length > 0) {
        toast({
          title: t('MeetingSummaryShareModal.noEmailFor', {
            names: result.withoutEmail.join(', '),
          }),
          status: 'warning',
          duration: 8000,
          isClosable: true,
        })
      }
      notifyMeetingSummaryShared(meetingId)
      modalProps.onClose()
    } catch (error) {
      handleError(error)
    } finally {
      setSending(false)
    }
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const result = await trpc.meeting.exportSummaryPdf.mutate({
        meetingId,
        intro,
        pieces: sentPieces,
      })
      downloadBase64File(result)
      notifyMeetingSummaryShared(meetingId)
    } catch (error) {
      handleError(error)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Modal size="6xl" closeOnOverlayClick={false} {...modalProps}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('MeetingSummaryShareModal.heading')}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Stepper index={activeStep} mb={6}>
            {wizardSteps.map((step) => (
              <Step key={step}>
                <StepIndicator>
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>
                <StepTitle>
                  {t(`MeetingSummaryShareModal.steps.${step}`)}
                </StepTitle>
                <StepSeparator />
              </Step>
            ))}
          </Stepper>

          <Loading size="sm" active={loading} />
          <TextErrors errors={[error]} />

          {activeStep === 0 && (
            <MeetingSummaryShareRecipients
              circlesIds={circlesIds}
              membersIds={membersIds}
              excludeMemberId={currentMember?.id}
              withoutAccountNames={withoutAccountNames}
              steps={summaryStepsList}
              excludedSteps={excludedSteps}
              onChangeCircles={handleChangeCircles}
              onChangeMembers={setMembersIds}
              onToggleStep={handleToggleStep}
            />
          )}

          {activeStep === 1 && board && pieces && (
            <Box overflowX="auto">
              <MeetingSummaryShareBoard
                pieces={visiblePieces}
                board={board}
                onChange={setBoard}
              />
            </Box>
          )}

          {activeStep === 2 && (
            <MeetingSummarySharePreview
              intro={intro}
              onChangeIntro={setIntro}
              pieces={previewPieces}
              recipientsNames={recipientsNames}
            />
          )}
        </ModalBody>

        <ModalFooter>
          <HStack w="100%">
            {activeStep > 0 && (
              <Button variant="ghost" onClick={goToPrevious}>
                {t('common.back')}
              </Button>
            )}
            <Spacer />
            {activeStep < 2 ? (
              <Button
                colorScheme="blue"
                isDisabled={!canGoNext}
                onClick={goToNext}
              >
                {t('common.next')}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  isLoading={downloading}
                  isDisabled={sending}
                  onClick={handleDownload}
                >
                  {t('MeetingSummaryShareModal.downloadPdf')}
                </Button>
                <Button
                  colorScheme="blue"
                  isLoading={sending}
                  isDisabled={downloading}
                  onClick={handleSend}
                >
                  {t('MeetingSummaryShareModal.send')}
                </Button>
              </>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
